/**
 * Parameters screen: everything the stack reads from .env, changeable here.
 *
 * The rule that shapes this page: a secret value is never sent to the browser.
 * For those the server says only whether something is set, and the field starts
 * empty — you can replace a password, you cannot read it back. Non-secret
 * values are shown as they are, because hiding a cron expression helps nobody.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  GROUP_LABELS, GROUP_ORDER,
  type SettingDescriptor, type SettingGroup, type SettingsSnapshot,
} from '../../shared/settings/types.ts';
import { useQuery } from '../transport/hooks.ts';
import { request } from '../transport/socket.ts';

function SourceBadge({ item }: { item: SettingDescriptor }): React.JSX.Element {
  if (item.envOnly) return <span className="tag">только .env</span>;
  if (item.source === 'mongo') return <span className="tag tag--accent">изменено в консоли</span>;
  if (item.source === 'env') return <span className="tag">из .env</span>;
  return <span className="tag">по умолчанию</span>;
}

/**
 * What the value would be without the current override, and what the code
 * falls back to when nothing is configured at all. Without this «не задано»
 * tells you nothing: unset means one thing for a cron expression and quite
 * another for a password.
 */
function Fallbacks({ item }: { item: SettingDescriptor }): React.JSX.Element | null {
  const parts: React.JSX.Element[] = [];

  if (item.source === 'mongo') {
    parts.push(
      <span key="env">
        в <span className="mono">.env</span>:{' '}
        {item.secret
          ? item.envIsSet ? 'заполнено' : 'пусто'
          : item.envValue
            ? <span className="mono">{item.envValue}</span>
            : 'пусто'}
      </span>,
    );
  }

  if (item.defaultValue !== undefined && item.defaultValue !== item.value) {
    parts.push(
      <span key="default">
        по умолчанию:{' '}
        {item.defaultValue === ''
          ? <span className="setting__empty">пусто</span>
          : <span className="mono">{item.defaultValue}</span>}
      </span>,
    );
  } else if (item.defaultValue === undefined && !item.envOnly && !item.isSet) {
    parts.push(<span key="nodefault">значения по умолчанию нет</span>);
  }

  if (parts.length === 0) return null;
  return (
    <p className="setting__meta setting__fallbacks">
      {parts.map((part, index) => (
        <span key={index}>
          {index > 0 && <span className="setting__sep"> · </span>}
          {part}
        </span>
      ))}
    </p>
  );
}

function Row({
  item, onSaved,
}: {
  item: SettingDescriptor;
  onSaved: () => void;
}): React.JSX.Element {
  const [draft, setDraft] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const editing = draft !== null;
  const locked = Boolean(item.envOnly);

  function startEdit(): void {
    setError(null);
    // A secret always starts empty: there is nothing to prefill, by design.
    setDraft(item.secret ? '' : (item.value ?? ''));
  }

  async function save(): Promise<void> {
    if (draft === null) return;
    setBusy(true);
    setError(null);
    try {
      await request('settings.set', { key: item.key, value: draft });
      setDraft(null);
      onSaved();
    } catch (err) {
      setError((err as { msg?: string }).msg ?? 'не удалось сохранить');
    } finally {
      setBusy(false);
    }
  }

  async function reset(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await request('settings.reset', { key: item.key });
      setDraft(null);
      onSaved();
    } catch (err) {
      setError((err as { msg?: string }).msg ?? 'не удалось сбросить');
    } finally {
      setBusy(false);
    }
  }

  function control(): React.JSX.Element {
    if (item.type === 'bool') {
      return (
        <select
          className="setting__input"
          value={draft ?? ''}
          onChange={(e) => setDraft(e.target.value)}
        >
          <option value="true">да</option>
          <option value="false">нет</option>
        </select>
      );
    }
    if (item.type === 'enum' && item.options) {
      return (
        <select
          className="setting__input"
          value={draft ?? ''}
          onChange={(e) => setDraft(e.target.value)}
        >
          {item.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    return (
      <input
        className="setting__input"
        type={item.secret ? 'password' : 'text'}
        value={draft ?? ''}
        autoComplete="off"
        spellCheck={false}
        placeholder={item.secret ? 'новое значение' : ''}
        onChange={(e) => setDraft(e.target.value)}
      />
    );
  }

  return (
    <div className={`setting${locked ? ' setting--locked' : ''}`}>
      <div className="setting__head">
        <span className="setting__label">{item.label}</span>
        <SourceBadge item={item} />
      </div>
      <div className="setting__key mono">{item.key}</div>
      {item.hint && <p className="setting__hint">{item.hint}</p>}
      {locked && <p className="setting__hint setting__hint--locked">{item.envOnly}</p>}

      <div className="setting__body">
        {editing ? (
          <>
            {control()}
            <button type="button" className="button button--primary" disabled={busy} onClick={() => void save()}>
              {busy ? '…' : 'Сохранить'}
            </button>
            <button type="button" className="button" disabled={busy} onClick={() => setDraft(null)}>
              Отмена
            </button>
          </>
        ) : (
          <>
            <span className="setting__value">
              {item.secret
                ? item.isSet
                  ? <span className="setting__filled">заполнено</span>
                  : <span className="setting__empty">не заполнено</span>
                : item.value
                  ? <span className="mono">{item.value}</span>
                  : <span className="setting__empty">не задано</span>}
            </span>
            {!locked && (
              <button type="button" className="button" onClick={startEdit}>
                {item.secret && item.isSet ? 'Заменить' : 'Изменить'}
              </button>
            )}
            {!locked && item.source === 'mongo' && (
              <button type="button" className="button button--danger" disabled={busy} onClick={() => void reset()}>
                Вернуть из .env
              </button>
            )}
          </>
        )}
      </div>

      {!editing && <Fallbacks item={item} />}

      {error && <p className="alert alert--error setting__error">{error}</p>}
      {item.source === 'mongo' && item.updatedAt && !editing && (
        <p className="setting__meta">
          изменено {new Date(item.updatedAt).toLocaleString('ru-RU')}
          {item.updatedBy && <> ключом <span className="mono">{item.updatedBy}</span></>}
        </p>
      )}
    </div>
  );
}

export function Settings(): React.JSX.Element {
  const { data, error, loading, reload } = useQuery<SettingsSnapshot>('settings.list');
  const [filter, setFilter] = useState('');
  const [group, setGroup] = useState<SettingGroup>('notify');

  const onSaved = useCallback(() => reload(), [reload]);
  useEffect(() => {
    // nothing to do; kept so the linter sees the dependency explicitly
  }, [data]);

  if (loading && !data) return <p className="muted">Загружаю параметры…</p>;
  if (error) return <p className="alert alert--error">Ошибка: {error.msg}</p>;
  if (!data) return <p className="muted">Нет данных.</p>;

  const needle = filter.trim().toLowerCase();
  const searching = needle.length > 0;

  const matches = (i: SettingDescriptor): boolean =>
    i.key.toLowerCase().includes(needle) || i.label.toLowerCase().includes(needle);

  // При поиске группы не ограничивают: искать параметр, заранее зная его
  // раздел, — это уже не поиск.
  const visible = searching
    ? data.items.filter(matches)
    : data.items.filter((i) => i.group === group);

  const countIn = (g: SettingGroup): number => data.items.filter((i) => i.group === g).length;
  const changedIn = (g: SettingGroup): number =>
    data.items.filter((i) => i.group === g && i.source === 'mongo').length;

  return (
    <section className="panel panel--fill">
      <header className="panel__head">
        <h2 className="panel__title">Параметры</h2>

      <p className="muted">
        Значения из <span className="mono">.env</span> можно переопределить здесь —
        они лягут в базу и будут иметь приоритет. Секретные значения на экран
        никогда не передаются: видно только, заполнено оно или нет.
      </p>

      {data.pendingRestart.length > 0 && (
        <p className="alert alert--info">
          Применятся после пересоздания контейнеров:{' '}
          <span className="mono">{data.pendingRestart.join(', ')}</span>
        </p>
      )}

      <input
        className="setting__input setting__filter"
        placeholder="Поиск по названию или переменной — по всем разделам"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      />
      </header>

      {!searching && (
        <nav className="subtabs" role="tablist">
          {GROUP_ORDER.map((g) => {
            const changed = changedIn(g);
            return (
              <button
                key={g}
                type="button"
                role="tab"
                aria-selected={group === g}
                className={`subtabs__item${group === g ? ' subtabs__item--active' : ''}`}
                onClick={() => setGroup(g)}
              >
                {GROUP_LABELS[g]}
                <span className="subtabs__count">{countIn(g)}</span>
                {changed > 0 && (
                  <span className="subtabs__dot" title={`изменено в консоли: ${changed}`} />
                )}
              </button>
            );
          })}
        </nav>
      )}

      {searching && (
        <p className="muted">
          Найдено: {visible.length}. Поиск идёт по всем разделам, поэтому вкладки
          скрыты — очистите поле, чтобы вернуться к разделам.
        </p>
      )}

      {/* Прокручивается только список: поиск и вкладки остаются на виду. */}
      <div className="panel__scroll">
        <div className="settings__list">
          {visible.map((item) => <Row key={item.key} item={item} onSaved={onSaved} />)}
          {visible.length === 0 && (
            <p className="muted">Ничего не найдено.</p>
          )}
        </div>
      </div>
    </section>
  );
}
